import { Link, useNavigate } from "react-router-dom";
import { Check, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { ClientOutcomes } from "@/components/ClientOutcomes";

const ROWS = [
  ["Who produces the numbers", "The vendor being paid", "An independent third party"],
  ["What's measured", "Impressions, clicks, calls, leads", "Spend vs. vendor claims vs. retained cases"],
  ["Invoice & contract review", "Not included", "Reconciled line-by-line"],
  ["Cost per retained case", "Rarely shown", "Calculated where data allows"],
  ["Vendor accountability", "Self-graded", "Independently scored"],
  ["Commitment", "Ongoing ad/retainer spend", "One-time assessment, no subscription"],
];

export default function Alternative() {
  const navigate = useNavigate();
  return (
    <div className="dark relative min-h-screen bg-[#0A1B2E] text-[#F5F5F5] font-body overflow-hidden" data-testid="alternative-page">
      <Seo title="Independent accountability vs. agency-reported dashboards | RetainProof" description="Why law firms use RetainProof as an independent alternative to relying on agency-reported marketing dashboards — reconciling spend, vendor claims and retained cases from a neutral vantage point." path="/vs" />
      <div className="grain-overlay" />

      <nav className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-body font-bold text-2xl tracking-tight" data-testid="brand-logo">
            <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-8 w-auto" />
            <span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-3">
            <Link to="/pricing"><Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">Pricing</Button></Link>
            <Link to="/about"><Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">About</Button></Link>
            <Link to="/register"><Button className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none font-medium" data-testid="alt-get-started">Get started</Button></Link>
          </div>
        </div>
      </nav>

      <section className="relative z-10 max-w-5xl mx-auto px-6 pt-36 pb-14">
        <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-5">The independent alternative</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight max-w-4xl" data-testid="alternative-hero-title">
          Agency-reported dashboards vs. <span className="italic text-[#D5A44A]">independent accountability.</span>
        </h1>
        <p className="mt-6 text-lg text-zinc-400 max-w-3xl leading-relaxed">
          Marketing agencies and lead generators report on the metrics they manage — and they're the same party being paid. RetainProof is the independent alternative: we reconcile what your firm spends, what vendors report, and the cases you actually retain, from a neutral vantage point.
        </p>
      </section>

      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="overflow-x-auto border border-white/10" data-testid="alternative-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="bg-white/5 text-left">
                  <th className="p-4 font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">&nbsp;</th>
                  <th className="p-4 font-display text-lg">Agency-reported dashboard</th>
                  <th className="p-4 font-display text-lg text-[#D5A44A]">RetainProof (independent)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {ROWS.map((r) => (
                  <tr key={r[0]}>
                    <td className="p-4 text-zinc-300 font-medium">{r[0]}</td>
                    <td className="p-4 text-zinc-400"><span className="inline-flex items-center gap-2"><X className="h-4 w-4 text-zinc-600 shrink-0" />{r[1]}</span></td>
                    <td className="p-4 text-zinc-200 bg-[#D5A44A]/5"><span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-[#0E7C7B] shrink-0" />{r[2]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-xs text-zinc-500">RetainProof is not a marketing agency and does not replace the vendors already supporting your firm. It provides an independent layer of analysis between marketing activity and firm leadership.</p>
        </div>
      </section>

      <section className="relative z-10 border-t border-white/10" data-testid="alternative-independence">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#0E7C7B] mb-5">How we stay independent</p>
          <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight max-w-3xl">We don't replace your vendors — we sit between them and your leadership.</h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-6">
            {[
              ["No vendor relationships", "We don't sell ads, run campaigns, or accept referral fees from the vendors we review. Nothing about our findings changes what we're paid."],
              ["Your data, your vantage point", "We reconcile the numbers your firm already owns — spend, invoices, calls and retained matters — rather than a dashboard the paid vendor built."],
              ["A one-time engagement", "An assessment is a fixed-scope review, not a subscription. Your vendors keep doing their work; we simply give leadership a neutral read on it."],
            ].map(([t, d]) => (
              <div key={t} className="border-l-2 border-[#D5A44A]/40 pl-5">
                <p className="font-display text-lg text-white">{t}</p>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ClientOutcomes />

      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]" data-testid="alternative-consult-banner">
        <div className="max-w-5xl mx-auto px-6 py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#0E7C7B] mb-3">Not sure yet? That's fine.</p>
            <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight">Is an assessment right for your firm?</h2>
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
              Take a free 15-minute call. We'll talk through your marketing setup and tell you honestly whether an independent review would surface anything worth acting on — no pressure, no obligation.
            </p>
          </div>
          <div className="shrink-0">
            <Link to="/consultation">
              <Button variant="outline" className="rounded-none h-12 px-8 text-base border-[#0E7C7B]/50 text-white bg-transparent hover:bg-[#0E7C7B]/10 group" data-testid="alternative-consult-cta">
                Book a 15-minute call <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="relative z-10 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">See what an independent review surfaces.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button onClick={() => navigate("/pricing")} className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-8 text-base font-medium group" data-testid="alternative-pricing-cta">
              See assessment options <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Link to="/consultation"><Button variant="outline" className="rounded-none h-12 px-8 text-base border-white/25 text-white bg-transparent hover:bg-white/5">Schedule a Consultation</Button></Link>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <span>© 2026 RetainProof™ · A product of CMD Marketing Enterprises LLC</span>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/pricing" className="hover:text-white">Pricing</Link>
            <Link to="/about" className="hover:text-white">About</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
